import { ArrowTopRightOnSquareIcon, CheckBadgeIcon, ChevronLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import { useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../catalyst/description-list'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table'
import { Text } from '../catalyst/text'
import { CATEGORY_LABELS, CERTIFIER_LABELS, FORM_LABELS, MODE_LABELS, NUTRIENT_LABELS, NUTRIENT_ORDER, SEGMENT_LABELS, useFertilizer } from '../lib/fertilizers'
import type { FertilizerProduct } from '../lib/types'
import { OrganicBadge } from './fertilizers-page'
import { Shell } from './shell'

/** What to tell the reader about the analysis when the figures alone would mislead. */
const ANALYSIS_NOTES: Partial<Record<FertilizerProduct['analysisStatus'], string>> = {
  VARIABLE: 'The supplier says the analysis varies by product or batch. Check the label.',
  NONE: 'The supplier gives no nutrient figures for this product.',
  NEEDS_REVIEW: 'We could not read all of the figures the supplier gives. Go by their wording.',
}

/** How much to trust the segment list: it is a compilation, never a recommendation. */
const FIT_BASIS_NOTES: Record<FertilizerProduct['fitBasis'], string> = {
  SUPPLIER_STATED: 'As stated by the supplier.',
  ASSESSED: 'Our own assessment from the supplier’s description. Not confirmed by the supplier or by growers.',
  GENERAL_PURPOSE: 'A general-purpose product, not tagged for particular crops.',
  UNKNOWN: 'Not stated.',
}

/** Whether a person on the team has checked this record. Anything but 'approved' reads as unreviewed. */
function ReviewNote({ product }: { product: FertilizerProduct }) {
  const reviewed = product.reviewStatus === 'approved'
  const when = product.reviewedAt && new Date(product.reviewedAt).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
  return (
    <div
      className={clsx(
        'mt-4 flex gap-2 rounded-lg px-3 py-2 text-sm/6',
        reviewed ? 'bg-zinc-950/[0.03] text-zinc-600 dark:bg-white/5 dark:text-zinc-400' : 'bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200',
      )}
    >
      {reviewed ? <CheckBadgeIcon className="mt-0.5 size-4 shrink-0" /> : <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />}
      <p>
        {reviewed ? (
          <>
            <span className="font-medium">Reviewed{when ? ` ${when}` : ''}. </span>
            Compiled from the supplier’s website and checked by the Farmula team.
          </>
        ) : (
          <>
            <span className="font-medium">Unreviewed. </span>
            Compiled from the supplier’s website; nobody on the Farmula team has checked it yet, so it may contain mistakes.
          </>
        )}{' '}
        Always confirm against the product label before applying.
      </p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <Subheading>{title}</Subheading>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function AnalysisSection({ product }: { product: FertilizerProduct }) {
  const nutrients = NUTRIENT_ORDER.filter((n) => product.analysis[n] !== undefined)
  const note = ANALYSIS_NOTES[product.analysisStatus]
  return (
    <Section title="Analysis">
      {nutrients.length > 0 && (
        <Table striped className="max-w-md">
          <TableHead>
            <TableRow>
              <TableHeader>Nutrient</TableHeader>
              <TableHeader className="text-right">Percent by weight</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {nutrients.map((n) => (
              <TableRow key={n}>
                <TableCell className="font-medium text-zinc-950 dark:text-white">{NUTRIENT_LABELS[n]}</TableCell>
                <TableCell className="text-right tabular-nums">{product.analysis[n]}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {product.analysisRaw && (
        <p className="mt-4 text-sm/6 text-zinc-500 dark:text-zinc-400">
          Supplier lists: <span className="font-medium text-zinc-950 dark:text-white">{product.analysisRaw}</span>
        </p>
      )}
      {note && <Text className="mt-2">{note}</Text>}
      {(product.analysis.P2O5 !== undefined || product.analysis.K2O !== undefined) && (
        <Text className="mt-2">Phosphorus and potassium are given as phosphate (P₂O₅) and potash (K₂O), as on fertilizer labels.</Text>
      )}
    </Section>
  )
}

function DetailsSection({ product }: { product: FertilizerProduct }) {
  const dash = <span className="text-zinc-500">—</span>
  const segments = product.segments.map((s) => SEGMENT_LABELS[s] ?? s)
  return (
    <Section title="Details">
      <DescriptionList>
        <DescriptionTerm>Type</DescriptionTerm>
        <DescriptionDetails>{CATEGORY_LABELS[product.category]}</DescriptionDetails>
        <DescriptionTerm>Form</DescriptionTerm>
        <DescriptionDetails>{product.forms.length > 0 ? product.forms.map((f) => FORM_LABELS[f]).join(', ') : dash}</DescriptionDetails>
        <DescriptionTerm>Applied as</DescriptionTerm>
        <DescriptionDetails>{product.applicationModes.length > 0 ? product.applicationModes.map((m) => MODE_LABELS[m]).join(', ') : dash}</DescriptionDetails>
        <DescriptionTerm>Release</DescriptionTerm>
        <DescriptionDetails>{product.release === 'CONTROLLED' ? 'Controlled or slow release' : 'Standard'}</DescriptionDetails>
        <DescriptionTerm>Organic</DescriptionTerm>
        <DescriptionDetails>
          <span className="inline-flex flex-wrap items-center gap-2">
            <OrganicBadge product={product} />
            {product.organicCertifiers.length > 0 && <span>{product.organicCertifiers.map((c) => CERTIFIER_LABELS[c] ?? c).join(', ')}</span>}
          </span>
        </DescriptionDetails>
        <DescriptionTerm>Listed for</DescriptionTerm>
        <DescriptionDetails>
          {segments.length > 0 ? segments.join(', ') : dash}
          <span className="mt-1 block text-zinc-500 dark:text-zinc-400">{FIT_BASIS_NOTES[product.fitBasis]}</span>
        </DescriptionDetails>
        {product.notes && (
          <>
            <DescriptionTerm>Notes</DescriptionTerm>
            <DescriptionDetails>{product.notes}</DescriptionDetails>
          </>
        )}
      </DescriptionList>
    </Section>
  )
}

export function FertilizerPage() {
  const { fertilizerId } = useParams<{ fertilizerId: string }>()
  const { fertilizer, loading, error } = useFertilizer(fertilizerId)
  const compiled = fertilizer ? new Date(fertilizer.fetchedAt).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }) : ''

  return (
    <Shell>
      <Link href="/fertilizers" className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        Fertilizers
      </Link>

      {error ? (
        <div className="mt-8 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading || !fertilizer ? (
        <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Heading>{fertilizer.name}</Heading>
              <p className="mt-1 text-sm/6 text-zinc-500 dark:text-zinc-400">
                {[fertilizer.supplier, fertilizer.brand && fertilizer.brand !== fertilizer.supplier ? fertilizer.brand : null].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
              <Badge color="blue">{CATEGORY_LABELS[fertilizer.category]}</Badge>
              <OrganicBadge product={fertilizer} />
            </div>
          </div>

          <ReviewNote product={fertilizer} />

          <AnalysisSection product={fertilizer} />
          <DetailsSection product={fertilizer} />

          <Section title="Source">
            <Text>
              Compiled from the supplier’s website in {compiled}, so it may have changed since. Check the product label before you buy or apply.
            </Text>
            <Link href={fertilizer.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm/6 font-medium">
              View on the supplier’s site
              <ArrowTopRightOnSquareIcon className="size-4" />
            </Link>
          </Section>
        </>
      )}
    </Shell>
  )
}
